import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { company } from "@/data/company";

const links = [
  { to: "/", label: "Úvod", end: true },
  { to: "/o-nas", label: "Proč my" },
  { to: "/kurzy", label: "Kurzy" },
  { to: "/kontakt", label: "Kontakt" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
    <div className="w-10 h-10 bg-gavito-green rounded-xl flex items-center justify-center group-hover:bg-gavito-navy transition-colors">
      <span className="font-heading font-black text-white text-xl leading-none">G</span>
    </div>
    <div className="leading-none">
      <div className="font-heading font-extrabold text-lg text-gavito-navy tracking-tight">GAVITO</div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-gavito-navy/50 font-semibold">vzdělávací agentura</div>
    </div>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-gavito-light/90 backdrop-blur-md border-b border-gavito-navy/5 py-0" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors ${
                    isActive ? "text-gavito-green" : "text-gavito-navy/80 hover:text-gavito-green"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              data-testid="nav-cta"
              className="bg-gavito-navy text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-gavito-green transition-colors"
            >
              Ozvěte se
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-gavito-navy p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-gavito-light border-t border-gavito-navy/10 px-6 py-3">
          <nav className="flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `py-3 text-base font-semibold border-b border-gavito-navy/10 ${
                    isActive ? "text-gavito-green" : "text-gavito-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a href={`tel:${company.phone}`} className="py-3 text-base font-bold text-gavito-green">
              {company.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
